import React, { useEffect, useState, useRef } from 'react';
import "react-image-gallery/styles/image-gallery.css";
import axios from 'axios';
import {
    CircularProgress,
    Button,
    TextField,
    Box,
    Fade,
    Alert,
    Collapse,
    Stepper,
    Step,
    StepLabel,
    Stack,
    InputLabel,
    MenuItem,
    FormControl,
    IconButton,
    Checkbox,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
    Typography,
    Card,
    CardContent,
    Drawer
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { LoadingCircle, EmailConfirmStep, PDFDownloadButton, WorkshopSelector, InfoPage } from './';
import Select from '@mui/material/Select';

const BACKEND_URL = process.env.REACT_APP_BACKEND;

const steps = ['Sinu andmed', 'Ülevaade', 'Emaili kinnitus'];

const sortByName = (a, b) => {
    const nameA = `${a.name ?? ''} ${a.name ?? ''}`.toLowerCase();
    const nameB = `${b.name ?? ''} ${b.name ?? ''}`.toLowerCase();
    return nameA.localeCompare(nameB);
};

export default function TeacherRegistration({ workshop, onSubmit }) {
    const [workshops, setWorkshops] = useState([]);
    const [favouriteWorkshops, setFavouriteWorkshops] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeStep, setActiveStep] = useState(0);
    const [userInfo, setUserInfo] = useState({ first_name: '', last_name: '', email: '', school: '', morning_workshop: '', evening_workshop: '' });

    const [firstNameValid, setFirstNameValid] = useState(false);
    const [firstNameError, setFirstNameError] = useState(false);
    const [firstNameFocus, setFirstNameFocus] = useState(false);

    const [lastNameValid, setLastNameValid] = useState(false);
    const [lastNameError, setLastNameError] = useState(false);
    const [lastNameFocus, setLastNameFocus] = useState(false);

    const [emailValid, setEmailValid] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [emailFocus, setEmailFocus] = useState(false);

    const [schoolValid, setSchoolValid] = useState(false);
    const [schoolError, setSchoolError] = useState(false);
    const [schoolFocus, setSchoolFocus] = useState(false);

    const [TTGStudent, setTTGStudent] = useState(false);

    const [firstWorkshopValid, setFirstWorkshopValid] = useState(false);
    const [firstWorkshopError, setFirstWorkshopError] = useState(false);
    const [firstWorkshopFocus, setFirstWorkshopFocus] = useState(false);

    const [secondWorkshopValid, setSecondWorkshopValid] = useState(false);
    const [secondWorkshopError, setSecondWorkshopError] = useState(false);
    const [secondWorkshopFocus, setSecondWorkshopFocus] = useState(false);

    const [canContinue, setCanContinue] = useState(false);

    const [emailToken, setEmailToken] = useState('');

    const [intervalId, setIntervalId] = useState(null);

    const [overbooked, setOverbooked] = useState(false);

    const [emailConfirmed, setEmailConfirmed] = useState(false);

    const [registrationStarted, setRegistrationStarted] = useState(false);
    const [registrationSucceeded, setRegistrationSucceeded] = useState(false);

    const [morningLocation, setMorningLocation] = useState(null);
    const [eveningLocation, setEveningLocation] = useState(null);

    const REFRESH_INTERVAL = 60000;

    const fetchData = async () => {
        try {
            const workshops_response = await axios.get(`${BACKEND_URL}/api/workshops?populate=*`);
            const workshop_data = workshops_response.data.data.sort(sortByName);

            const availability_response = await axios.get(`${BACKEND_URL}/api/workshops/available`);
            const availability_data = availability_response.data;

            console.log(JSON.stringify(availability_data));

            let aggregate_data = workshop_data.map((workshop) => ({
                ...workshop,
                availableSpots: availability_data[workshop.documentId].sessions,
                sessionCount: availability_data[workshop.documentId].sessionCount
            }));

            aggregate_data.forEach((workshop, i) => {
                if (!workshop.availableSpots.morning) {
                    aggregate_data[i].availableSpots.morning = 0;
                }
                if (!workshop.availableSpots.evening) {
                    aggregate_data[i].availableSpots.evening = 0;
                }
            })

            console.log(aggregate_data);

            setWorkshops(aggregate_data);
            setLoading(false);

            return aggregate_data;

        } catch (err) {
            console.error(err);
            setLoading(false);
        }
    }

    useEffect(() => {

        fetchData();

        setIntervalId(setInterval(fetchData, REFRESH_INTERVAL));

        return () => {
            clearInterval(intervalId);
        }

    }, []);

    useEffect(() => {
        if (userInfo.first_name !== '' && userInfo.first_name) {
            setFirstNameValid(true);
        } else {
            setFirstNameValid(false);
        }
    }, [userInfo.first_name, firstNameFocus]);

    useEffect(() => {
        if (userInfo.last_name !== '' && userInfo.last_name) {
            setLastNameValid(true);
        } else {
            setLastNameValid(false);
        }
    }, [userInfo.last_name, lastNameFocus]);

    useEffect(() => {
        if (isValidEmail()) {
            setEmailValid(true);
        } else {
            setEmailValid(false);
        }
    }, [userInfo.email, emailFocus]);

    useEffect(() => {
        if (userInfo.morning_workshop !== '' && userInfo.morning_workshop) {
            setFirstWorkshopValid(true);
        } else {
            setFirstWorkshopValid(false);
        }
    }, [userInfo.morning_workshop, firstWorkshopFocus]);

    useEffect(() => {
        if (userInfo.evening_workshop !== '' && userInfo.evening_workshop) {
            setSecondWorkshopValid(true);
        } else {
            setSecondWorkshopValid(false);
        }
    }, [userInfo.evening_workshop, secondWorkshopFocus]);

    useEffect(() => {
        if (userInfo.school !== '' && userInfo.school) {
            setSchoolValid(true);
        } else {
            setSchoolValid(false);
        }
    }, [userInfo, schoolFocus]);

    useEffect(() => {
        if (checkCanContinue()) {
            setCanContinue(true);
        } else {
            setCanContinue(false);
        };
    }, [userInfo, activeStep, emailValid, schoolValid, firstNameValid, lastNameValid, firstWorkshopValid, secondWorkshopValid, TTGStudent]);

    useEffect(() => {
        if (firstNameFocus) {
            setFirstNameError(false);
        }
    }, [firstNameFocus]);

    useEffect(() => {
        if (lastNameFocus) {
            setLastNameError(false);
        }
    }, [lastNameFocus]);

    useEffect(() => {
        if (emailFocus) {
            setEmailError(false);
        }
    }, [emailFocus]);

    useEffect(() => {
        if (schoolFocus) {
            setSchoolError(false);
        }
    }, [schoolFocus]);

    useEffect(() => {
        if (firstWorkshopFocus) {
            setFirstWorkshopError(false);
        }
    }, [firstWorkshopFocus]);

    useEffect(() => {
        if (secondWorkshopFocus) {
            setSecondWorkshopError(false);
        }
    }, [secondWorkshopFocus]);

    useEffect(() => {
        if (TTGStudent) {
            handleUserChange('school', 'Tartu Tamme Gümnaasium');
            setSchoolValid(true);
            setSchoolError(false);
        } else {
            handleUserChange('school', '');
            setSchoolValid(true);
        }
    }, [TTGStudent]);

    useEffect(() => {
        checkForOverbooking(workshops);
    }, [userInfo.morning_workshop, userInfo.evening_workshop, overbooked, activeStep]);

    const handleEmailConfirmed = (morningLocation, eveningLocation) => {
        setMorningLocation(morningLocation);
        setEveningLocation(eveningLocation);
        setEmailConfirmed(true);

    }

    const handleTTGStudentCheckedChange = () => {
        setTTGStudent(!TTGStudent);
    }

    const handleNext = () => {

        if (!canContinue) {

            if (!firstNameValid) {
                setFirstNameError(true);
            }

            if (!lastNameValid) {
                setLastNameError(true);
            }

            if (!emailValid) {
                setEmailError(true);
            }

            if (!schoolValid) {
                setSchoolError(true);
            }

            if (!firstWorkshopValid) {
                setFirstWorkshopError(true);
            }

            if (!secondWorkshopValid) {
                setSecondWorkshopError(true);
            }

            return;

        } else {
            if (activeStep === steps.length - 1) {
                handleRegistration();
            }
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setActiveStep(0);
    };

    const handleUserChange = (field, value) => {
        setUserInfo((prev) => ({ ...prev, [field]: value }));
    }

    const getWorkshopName = (workshopId) => {
        const workshop = workshops.find(ws => ws.documentId === workshopId);
        return workshop?.name ?? '-';
    }

    const isUserSessionAvailable = (workshopId, timeslot) => {
        const workshop = workshops.find(ws => ws.documentId === workshopId);
        const spotsFull = workshop.availableSpots[timeslot] < 1;
        let otherSessionSame = false;

        if (workshopId === userInfo[`${timeslot === 'morning' ? 'evening_workshop' : 'morning_workshop'}`]) {
            otherSessionSame = true;
        }

        return spotsFull || otherSessionSame;
    }

    const checkForOverbooking = (workshops) => {

        if (userInfo.morning_workshop && userInfo.morning_workshop !== '') {
            const morning_workshop = workshops.find(ws => ws.documentId === userInfo.morning_workshop);

            if (morning_workshop.availableSpots.morning < 1) {
                setOverbooked(true);
                setActiveStep(0);
                return true;
            }
        }

        if (userInfo.evening_workshop && userInfo.evening_workshop !== '') {
            const evening_workshop = workshops.find(ws => ws.documentId === userInfo.evening_workshop);

            if (evening_workshop.availableSpots.evening < 1) {
                setOverbooked(true);
                setActiveStep(0);
                return true;
            }
        }

        setOverbooked(false);
        return false;

    }

    const handleRegistration = async () => {

        if (registrationStarted) {
            return;
        }

        setRegistrationStarted(true);

        const updatedWorkshops = await fetchData();

        const isOverbooked = checkForOverbooking(updatedWorkshops);

        if (!isOverbooked) {
            const requestData = {
                parentBookingData: userInfo,
                childBookingData: []
            }

            try {
                const res = await axios.post(`${BACKEND_URL}/api/bookings/register`, requestData);
                if (res.status === 200) {
                    setEmailToken(res.data.parent.email_token);
                    handleNext();

                    setRegistrationStarted(false);
                    setRegistrationSucceeded(true);

                    clearInterval(intervalId);
                }
            } catch (err) {

                setRegistrationStarted(false);
                setRegistrationSucceeded(false);

                console.error(err);
            }
        } else {
            setRegistrationStarted(false);
            setRegistrationSucceeded(false);
        }
    }

    const isValidEmail = () => {
        const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const status = regex.test(userInfo.email);
        return status;
    }

    const canContinueFromUserInfo = () => {
        if (firstNameValid && lastNameValid && emailValid && schoolValid && firstWorkshopValid && secondWorkshopValid && !overbooked) {
            return true;
        } else {
            return false;
        }
    }

    const checkCanContinue = () => {
        if (activeStep === 0) {
            return canContinueFromUserInfo();
        } else {
            return true;
        }

    }
    
    const [infoWorkshop, setInfoWorkshop] = useState(null);
    const [open, setOpen] = useState(false);

    const handleOpenWorkshopInfo = (documentId) => {
        const workshop = workshops.find(
            (ws) => ws.documentId === documentId
        );

        if (!workshop) {
            console.error(
                'Could not find workshop:',
                documentId
            );
            return;
        }

        setInfoWorkshop(workshop);
        setOpen(true);
    };
    
    const toggleDrawer = (newOpen) => () => {
        setOpen(newOpen);
    }

    const handleToggleFavourite = (workshopId) => {
        setFavouriteWorkshops((prev) => {
            if (prev.includes(workshopId)) {
                // Already a favourite → remove it
                return prev.filter((id) => id !== workshopId);
            }
    
            // Not a favourite → add it
            return [...prev, workshopId];
        });
    };


    const stepContent = [
        // First step - user info
        <Box display="flex" flexDirection="column" gap={2}>
            <TextField
                required
                label="Eesnimi"
                value={userInfo.first_name}
                error={firstNameError}
                onFocus={() => setFirstNameFocus(true)}
                onBlur={() => setFirstNameFocus(false)}
                onChange={(e) => handleUserChange("first_name", e.target.value)}
            />
            <TextField
                required
                label="Perenimi"
                value={userInfo.last_name}
                error={lastNameError}
                onFocus={() => setLastNameFocus(true)}
                onBlur={() => setLastNameFocus(false)}
                onChange={(e) => handleUserChange("last_name", e.target.value)}
            />
            <TextField
                required
                label="Email"
                value={userInfo.email}
                error={emailError}
                onFocus={() => setEmailFocus(true)}
                onBlur={() => setEmailFocus(false)}
                onChange={(e) => handleUserChange("email", e.target.value)}
            />
            <TextField
                required
                label="Kool"
                value={userInfo.school}
                error={schoolError}
                disabled={TTGStudent}
                onFocus={() => setSchoolFocus(true)}
                onBlur={() => setSchoolFocus(false)}
                onChange={(e) => handleUserChange("school", e.target.value)}
            />
            <FormControlLabel control={<Checkbox checked={TTGStudent} onChange={handleTTGStudentCheckedChange} />} label="Olen Tartu Tamme Gümnaasiumi õpilane" />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5, mt: 2, }} >
                <WorkshopSelector
                    title="Hommikune töötuba"
                    timeslot="morning"
                    workshops={workshops}
                    selectedWorkshop={userInfo.morning_workshop}
                    favourites={favouriteWorkshops}
                    onSelect={(workshopId) => handleUserChange( 'morning_workshop', workshopId ) }
                    onToggleFavourite={handleToggleFavourite}
                    onOpenInfo={handleOpenWorkshopInfo}
                    isWorkshopUnavailable={ isUserSessionAvailable } />
                <WorkshopSelector
                    title="Õhtune töötuba"
                    timeslot="evening" workshops={workshops}
                    selectedWorkshop={userInfo.evening_workshop}
                    favourites={favouriteWorkshops}
                    onSelect={(workshopId) => handleUserChange( 'evening_workshop', workshopId ) }
                    onToggleFavourite={handleToggleFavourite}
                    onOpenInfo={handleOpenWorkshopInfo}
                    isWorkshopUnavailable={ isUserSessionAvailable } />
            </Box>
            <Drawer
                anchor="bottom"
                open={open}
                onClose={() => setOpen(false)}
            >
                <Box
                    sx={{
                        height: '80vh',
                        width: {
                            xs: '90vw',
                            md: '80vw',
                        },
                        margin: '0 auto',
                        overflowY: 'auto',
                    }}
                >
                    {infoWorkshop && (
                        <InfoPage
                            workshop={infoWorkshop}
                        />
                    )}
                </Box>
            </Drawer>
        </Box>,

        // Second step - confirmation
        <Box>
            <Stack>
                Teie registreering:
                <List>
                    <ListItem
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                md: "0.25fr 1fr 1fr 1.5fr 1.5fr",
                                xs: "0.25fr 1fr 1fr"
                            },
                            gap: 2,
                            alignItems: "center",
                            backgroundColor: '#1976d2',
                            color: 'white',
                            border: '0px solid',
                            borderRadius: '0.5vh 0.5vh 0 0',
                        }}
                    >
                        <ListItemText primary='Nr' />
                        <ListItemText primary='Eesnimi' />
                        <ListItemText primary='Perenimi' />
                        <ListItemText primary='Hommikune töötuba' sx={{ gridColumn: { xs: '1 / span 2', md: '4' } }} />
                        <ListItemText primary='Õhtune töötuba' sx={{ gridColumn: { xs: '3 / span 2', md: '5' } }} />
                    </ListItem>
                    <ListItem
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                md: "0.25fr 1fr 1fr 1.5fr 1.5fr",
                                xs: "0.25fr 1fr 1fr"
                            },
                            gap: 2,
                            alignItems: "center",
                        }}
                    >
                        <ListItemText primary='1' />
                        <ListItemText primary={userInfo.first_name} />
                        <ListItemText primary={userInfo.last_name} />
                        <ListItemText primary={getWorkshopName(userInfo.morning_workshop)} sx={{ gridColumn: { xs: '1 / span 2', md: '4' } }} />
                        <ListItemText primary={getWorkshopName(userInfo.evening_workshop)} sx={{ gridColumn: { xs: '3 / span 2', md: '5' } }} />
                    </ListItem>
                </List>

            </Stack>
        </Box>,

        // Third step - waiting for email confirmation
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
            <EmailConfirmStep email_token={emailToken} onSuccess={handleEmailConfirmed} />
            <Collapse in={emailConfirmed}>
                <PDFDownloadButton workshops={workshops} userInfo={userInfo} plannedBookings={[]} morningLocation={morningLocation} eveningLocation={eveningLocation} />
            </Collapse>
        </Box>

    ]

    if (loading) return <LoadingCircle />

    return (
        <Box sx={{ maxWidth: { xl: '65%', md: '95%' }, margin: '2vh auto 10vh auto' }}>
            <Fade in={overbooked} timeout={600}>
                <Box
                    sx={{
                        position: 'fixed',
                        top: 16,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1300, // higher than most MUI elements
                        width: 'auto',
                        maxWidth: '90%',
                    }}
                >
                    <Alert severity="error" variant="filled">
                        Mõned sinu broneeritud töötoad on vahepeal täitunud. Palun kustuta punaselt märgitud registreeringud ja loo need uuesti.
                    </Alert>
                </Box>
            </Fade>
            <Box sx={{ display: 'flex', justifyContent: 'right' }}>
                <IconButton aria-label="go back" href='/register' size='large'>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Stepper activeStep={activeStep} sx={{ mb: 9, mt: 3 }}>
                {steps.map((label, index) => {
                    const stepProps = {};
                    const labelProps = {};
                    return (
                        <Step key={label} {...stepProps}>
                            <StepLabel {...labelProps}>{label}</StepLabel>
                        </Step>
                    );
                })}
            </Stepper>
            {/* <Typography variant="h5"
                component="h2"
                sx={{
                    fontWeight: 200,
                    mb: 3,
                    textAlign: 'left',
                    color: 'text.primary',
                    lineHeight: 1.3,
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                }}>
                {steps[activeStep]}
            </Typography> */}
            {stepContent[activeStep]}
            {activeStep === steps.length ? (
                <React.Fragment>
                    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                        <Box sx={{ flex: '1 1 auto' }} />
                        <Button onClick={handleReset}>Reset</Button>
                    </Box>
                </React.Fragment>
            ) : (
                <React.Fragment>
                    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                        <Button
                            color="inherit"
                            disabled={activeStep === 0 || activeStep === 2}
                            onClick={handleBack}
                            sx={{ mr: 1, opacity: (activeStep !== 0 && activeStep !== 2) ? 1 : 0 }}
                        >
                            Tagasi
                        </Button>
                        <Box sx={{ flex: '1 1 auto' }} />
                        <Button
                            disabled={activeStep === 2}
                            sx={{ opacity: (activeStep !== 2) ? 1 : 0 }}
                            variant={activeStep === 1 ? 'contained' : 'text'}
                            onClick={activeStep === 1 ? handleRegistration : handleNext}>
                            {activeStep === steps.length - 2 ? 'Kinnita registreerimine' : 'Edasi'}
                        </Button>
                        <Fade
                            in={registrationStarted}
                            style={{
                                transitionDelay: loading ? '800ms' : '0ms',
                            }}
                            unmountOnExit
                        >
                            <CircularProgress />
                        </Fade>

                    </Box>
                </React.Fragment>
            )}
        </Box>
    );
}
